import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import { listExamConfigs, listSubjectConfigs, getExamMarkGrid, saveExamMarks, deleteExamMarks } from "../../api/examApi";
import "../Attendance/Attendance.css";

const subjectApi = createLookupApi("subjects");
const academicYearApi = createLookupApi("academic-years");

/**
 * Per-subject mark entry grid — the short-code columns shown are exactly the
 * ones configured for that subject (see ExamSubjectConfigPage).
 *
 * Carries its own "Select Year" academic-year selector, matching the old
 * app's real `exam-mark-input.html` template (posts an explicit
 * `academicYear` alongside class/exam/subject) — previously missing here,
 * which silently defaulted the grid/save to the institute's *current*
 * academic-year setting instead of the year the marks actually belong to
 * (same recurring bug class as `StudentMonthlyDetailsPage`/
 * `ExamMarkProcessPage` elsewhere in this migration).
 *
 * Also carries the old app's real "Select Group" filter (`Select Year,
 * Select Section, Select Exam, Select Group, Select Subject` — 5 filters in
 * the real template; this page previously had only 4, silently showing every
 * student in the class-configuration for every subject). The Group dropdown
 * is sourced from `GET /api/group-configurations/by-class/:id` (that class's
 * already-configured groups), same as `SubjectConfigurationPage`. The grid
 * itself is now scoped server-side by that group + the subject's real
 * `SubjectConfiguration` status (Compulsory/Choosable/Group Based/Elective/
 * Uncountable) — see `ExamMarkServiceImpl#resolveScopedStudents` javadoc for
 * the exact mapping, including the deliberate permissive fallback when a
 * class+group+subject combination has no Subject Setup row at all.
 */
export default function ExamMarkEntryPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [subjectConfigs, setSubjectConfigs] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [subjectNames, setSubjectNames] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([listClassConfigurations(), subjectApi.list(), academicYearApi.list()]).then(([configs, subjects, years]) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
      const map = {};
      subjects.forEach((s) => (map[s.id] = s.name));
      setSubjectNames(map);
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
    listGroupConfigurationsByClassId(cc.classId).then((list) => {
      setGroups(list);
      setGroupId(list.length > 0 ? String(list[0].groupId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId) {
      setSubjectConfigs([]);
      return;
    }
    listSubjectConfigs(examConfigId).then((list) => {
      setSubjectConfigs(list);
      setSubjectId(list.length > 0 ? String(list[0].subjectId) : "");
    });
  }, [examConfigId]);

  const activeSubjectConfig = subjectConfigs.find((sc) => String(sc.subjectId) === subjectId);

  const loadGrid = () => {
    if (!examConfigId || !subjectId || !classConfigurationId || !groupId || !academicYear) return;
    setLoading(true);
    setError("");
    getExamMarkGrid(examConfigId, subjectId, classConfigurationId, groupId, academicYear)
      .then(setRows)
      .catch(() => setError("Failed to load mark grid."))
      .finally(() => setLoading(false));
  };

  useEffect(loadGrid, [examConfigId, subjectId, classConfigurationId, groupId, academicYear]);

  const setSlot = (identificationId, slot, value) => {
    setRows((prev) => prev.map((r) => (r.identificationId === identificationId ? { ...r, [`shortCode${slot}`]: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const entries = rows.map((r) => ({
        identificationId: r.identificationId,
        shortCode1: Number(r.shortCode1), shortCode2: Number(r.shortCode2), shortCode3: Number(r.shortCode3), shortCode4: Number(r.shortCode4),
        shortCode5: Number(r.shortCode5), shortCode6: Number(r.shortCode6), shortCode7: Number(r.shortCode7), shortCode8: Number(r.shortCode8),
      }));
      const updated = await saveExamMarks({
        examConfigId: Number(examConfigId),
        subjectId: Number(subjectId),
        classConfigurationId: Number(classConfigurationId),
        groupId: Number(groupId),
        academicYear,
        entries,
      });
      setRows(updated);
      setSuccess("Marks saved.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save marks.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete the entered mark for "${row.studentName}"? This cannot be undone.`)) return;
    setError("");
    try {
      await deleteExamMarks([row.markInputId]);
      setSuccess("Mark deleted.");
      loadGrid();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete mark.");
    }
  };

  const configuredSlots = activeSubjectConfig ? activeSubjectConfig.markConfigs.map((mc) => mc.defaultId) : [];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Mark Input</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.length === 0 && <option value="">No groups configured</option>}
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Subject
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
            {subjectConfigs.length === 0 && <option value="">No subjects configured</option>}
            {subjectConfigs.map((sc) => (
              <option key={sc.subjectId} value={sc.subjectId}>
                {subjectNames[sc.subjectId] || sc.subjectName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Roll</th>
                <th>Student</th>
                {configuredSlots.map((slot) => (
                  <th key={slot}>
                    {activeSubjectConfig.markConfigs.find((mc) => mc.defaultId === slot).shortCodeTitle}
                  </th>
                ))}
                <th>Total</th>
                <th>Grade</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.identificationId}>
                  <td>{row.studentRoll}</td>
                  <td>{row.studentName}</td>
                  {configuredSlots.map((slot) => (
                    <td key={slot}>
                      <input
                        type="number"
                        style={{ width: 60 }}
                        value={row[`shortCode${slot}`]}
                        onChange={(e) => setSlot(row.identificationId, slot, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    {row.totalScore}/{row.totalMark}
                  </td>
                  <td>{row.letterGrade}</td>
                  <td className="actions-col">
                    {row.markInputId && (
                      <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || rows.length === 0}>
              {saving ? "Saving..." : "Save Marks"}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
